import { EventClickArg, EventDropArg, EventInput, EventSourceInput } from '@fullcalendar/core';
import interactionPlugin, { DateClickArg } from '@fullcalendar/interaction';
import momentTimezonePlugin from '@fullcalendar/moment-timezone';
import FullCalendar from '@fullcalendar/react';
import { ResourceSourceInput } from '@fullcalendar/resource';
import resourceTimeGridPlugin from '@fullcalendar/resource-timegrid';
import scrollGridPlugin from '@fullcalendar/scrollgrid';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import Appointment from '@spike/appointment-model';
import { BlockCalendar } from '@spike/block-calendar-model';
import clsx from 'clsx';
import { getStaffSlotsByDate } from 'components/StaffSchedule/utils/StaffScheduleUtils';
import { BusinessAreaType, SERVICES_BUSINESS_AREAS } from 'model/Service';
import { StaffSchedule, StaffSlot, StaffSlots } from 'model/Staff';
import moment, { Moment } from 'moment-timezone';
import { FunctionComponent, createRef, useCallback, useEffect, useMemo } from 'react';
import {
    convertToBlockCalendarEvent,
    convertToEvent,
    convertToSlotEvent,
    convertToTempEvent,
    getResourceBusinessHours,
    renderEvent
} from '../FullCalendar/FullCalendarUtils';
import NowIndicatorFullCalendar from '../FullCalendar/NowIndicatorFullCalendar';
import { useFullCalendarStyles } from '../FullCalendar/StylesFullCalendar';
import { TempNewBooking } from '../FullCalendar/model';
import ResourceHeaderFullCalendar from './ResourceHeaderFullCalendar';

interface DayFullCalendarProps {
    date: Moment;
    timeZone: string;
    showPetsCountBadge: boolean;
    isSlotsView: boolean;
    appointments: Array<Appointment>;
    blocks: Array<BlockCalendar>;
    multipleBookingsIds: Array<number>;
    staff: Array<StaffResource>;
    tempNewBooking?: TempNewBooking;
    loadingAppointmentIds?: Array<number>;
    businessArea: BusinessAreaType;
    readOnly: boolean;
    onClickAppointment?: (id: number) => void;
    onClickBlockCalendar?: (id: number) => void;
    onClickStaffSlot?: (slot: StaffSlot, staffId: number, element: HTMLElement) => void;
    onRescheduled?: (appointmentId: number, at: Moment, revert: () => void, staffId?: number) => void;
    onRescheduledBlocked?: (blocked: BlockCalendar, revert: () => void) => void;
    onBook?: (at: Moment, staffId?: number) => void;
    onScroll: () => void;
}

export interface StaffResource {
    id: number;
    firstName: string;
    lastName: string;
    avatar: string;
    schedule: StaffSchedule;
    slots: StaffSlots;
    staffColor: string | null;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        container: {
            'minWidth': '100%',
            '& .fc .fc-timegrid-slot': {
                [theme.breakpoints.down('sm')]: {
                    height: '22px !important'
                },
                [theme.breakpoints.only('md')]: {
                    height: '33px !important'
                },
                [theme.breakpoints.only('lg')]: {
                    height: '42px !important'
                },
                [theme.breakpoints.only('xl')]: {
                    height: '49px !important'
                }
            },
            '& .fc .fc-timegrid-slot-label': {
                border: 'none !important'
            },
            '& .fc-scrollgrid-section-body.fc-scrollgrid-section-liquid > td:first-child .fc-scroller': {
                '&::-webkit-scrollbar': {
                    display: 'none'
                },
                '-ms-overflow-style': 'none',
                'scrollbar-width': 'none'
            },
            '& .fc-scrollgrid-section.fc-scrollgrid-section-header > th:last-child .fc-scroller': {
                '&::-webkit-scrollbar': {
                    display: 'none'
                },
                '-ms-overflow-style': 'none',
                'scrollbar-width': 'none'
            },
            '& .fc-col-header-cell-cushion': {
                padding: 0
            },
            '& .fc-direction-ltr .fc-timegrid-col-events': {
                margin: '0px 6px'
            }
        },
        slotsViewContainer: {
            '& .fc-timegrid-event-harness-inset': {
                left: '0px !important',
                right: '0px !important'
            },
            '& .fc .fc-timegrid-slot': {
                height: '32px !important',

                [theme.breakpoints.up('md')]: {
                    height: '44px !important'
                }
            }
        },
        resourceHeader: {
            backgroundColor: 'white',
            borderTop: 'none !important',
            borderLeft: 'none !important',
            borderRight: 'none !important',
            borderBottom: '1px solid #D8D8D8 !important',
            verticalAlign: 'middle !important',
            border: '1px solid red !important'
        },
        nowIndicator: {
            '&.fc-timegrid-now-indicator-arrow': {
                border: 'none',
                width: '100%'
            },
            '&.fc-timegrid-now-indicator-line': {
                border: '1px solid black !important',
                height: '0px',
                backgroundColor: 'black'
            }
        }
    })
);

export const DayFullCalendar: FunctionComponent<DayFullCalendarProps> = props => {
    const classes = useStyles();
    const fullcalendarClasses = useFullCalendarStyles();

    const wrapperRef = createRef<HTMLDivElement>();
    const fullCalendarRef = createRef<FullCalendar>();
    const getCalendarNow = useCallback(() => moment.tz(props.timeZone).format(), [props.timeZone]);

    useEffect(() => {
        fullCalendarRef.current?.getApi().gotoDate(props.date.toDate());
    }, [props.date]);

    useEffect(() => {
        props.tempNewBooking &&
            fullCalendarRef.current?.getApi().scrollToTime({ hour: props.tempNewBooking?.at.get('hour') });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.tempNewBooking]);

    // Handle fullcalendar scroll event to close edit slot modal
    useEffect(() => {
        if (wrapperRef.current) {
            const scrollEl = wrapperRef.current.querySelector('.fc-scroller-liquid-absolute');

            if (scrollEl) {
                scrollEl.addEventListener('scroll', props.onScroll);

                return () => {
                    scrollEl.removeEventListener('scroll', props.onScroll);
                };
            }
        }
    }, []);

    const today = moment().tz(props.timeZone);

    const scrollTime = today.format('HH:00:00');

    const events: EventSourceInput = useMemo(() => {
        if (props.isSlotsView) {
            return props.staff.reduce<Array<EventInput>>((acc, staff) => {
                const slots = getStaffSlotsByDate(staff.slots, props.date);

                for (let i = 0; i < slots.length; i++) {
                    acc.push(convertToSlotEvent(slots[i], props.date, staff.id, props.timeZone));
                }

                return acc;
            }, [] as Array<EventInput>);
        }

        const events = props.appointments
            .filter(appointment => {
                if (props.businessArea === SERVICES_BUSINESS_AREAS.GROOMING) {
                    return appointment.services.some(
                        service =>
                            service.businessArea?.id === SERVICES_BUSINESS_AREAS.DENTAL ||
                            service.businessArea?.id === SERVICES_BUSINESS_AREAS.GROOMING
                    );
                }

                return appointment.services.some(service => service.businessArea?.id === props.businessArea);
            })
            .map(appointment => convertToEvent(appointment, props.timeZone, 'daily', props.multipleBookingsIds));

        const blocks = props.blocks.map(block => convertToBlockCalendarEvent(block, props.timeZone));

        return props.tempNewBooking
            ? [...events, ...blocks, convertToTempEvent(props.tempNewBooking, props.timeZone)]
            : [...events, ...blocks];
    }, [
        props.isSlotsView,
        props.appointments,
        props.staff,
        props.blocks,
        props.tempNewBooking,
        props.timeZone,
        props.businessArea
    ]);

    const unassignedAppointmentsCount = useMemo(() => {
        return props.appointments
            .filter(appointment => {
                if (props.businessArea === SERVICES_BUSINESS_AREAS.GROOMING) {
                    return appointment.services.some(
                        service =>
                            service.businessArea?.id === SERVICES_BUSINESS_AREAS.DENTAL ||
                            service.businessArea?.id === SERVICES_BUSINESS_AREAS.GROOMING
                    );
                }

                return appointment.services.some(service => service.businessArea?.id === props.businessArea);
            })
            .filter(
                appointment =>
                    appointment.duration.from.isSame(props.date, 'day') &&
                    appointment.services.every(service => !service.staff?.id)
            ).length;
    }, [props.appointments, props.date, props.businessArea]);

    const resources: ResourceSourceInput = useMemo(() => {
        const hasUnassignedAppointments = unassignedAppointmentsCount > 0;
        const orderOffset = hasUnassignedAppointments ? 1 : 0;

        const staffResources = props.staff.map((member, index) => ({
            id: member.id.toString(),
            order: index + orderOffset,
            businessHours: getResourceBusinessHours(props.date, member.schedule, props.timeZone),
            title: `${member.firstName} ${member.lastName}`,
            firstName: member.firstName,
            lastName: member.lastName,
            avatar: member.avatar,
            slots: member.slots,
            staffColor: member.staffColor,
            isUnassigned: false,
            appointmentsCount: props.appointments
                .filter(appointment => {
                    if (props.businessArea === SERVICES_BUSINESS_AREAS.GROOMING) {
                        return appointment.services.some(
                            service =>
                                service.businessArea?.id === SERVICES_BUSINESS_AREAS.DENTAL ||
                                service.businessArea?.id === SERVICES_BUSINESS_AREAS.GROOMING
                        );
                    }

                    return appointment.services.some(service => service.businessArea?.id === props.businessArea);
                })
                .filter(
                    appointment =>
                        appointment.duration.from.isSame(props.date, 'day') &&
                        appointment.services.some(service => service.staff?.id === member.id)
                ).length
        }));

        if (!hasUnassignedAppointments) {
            return staffResources;
        }

        const unassignedResource = {
            id: '0',
            order: 0,
            businessHours: undefined,
            title: 'Unassigned',
            firstName: 'Unassigned',
            lastName: '',
            avatar: '',
            slots: {},
            staffColor: null,
            isUnassigned: true,
            appointmentsCount: unassignedAppointmentsCount
        };

        return [unassignedResource, ...staffResources];
    }, [props.appointments, props.date, props.staff, props.businessArea, unassignedAppointmentsCount]);

    const eventClickHandler = (args: EventClickArg) => {
        args.event.extendedProps.type === 'appointment' &&
            props.onClickAppointment &&
            props.onClickAppointment(args.event.extendedProps.appointmentId);

        args.event.extendedProps.type === 'block' &&
            props.onClickBlockCalendar &&
            props.onClickBlockCalendar(args.event.extendedProps.blockId);

        args.event.extendedProps.type === 'slot' &&
            props.onClickStaffSlot &&
            props.onClickStaffSlot(args.event.extendedProps.slot, args.event.extendedProps.staffId, args.el);
    };

    const dropHandler = (args: EventDropArg, timeZone: string) => {
        if (args.event.extendedProps.type === 'block') {
            const blockedTime: BlockCalendar = props.blocks.find(
                block => block.id === args.event.extendedProps.blockId
            )!;
            blockedTime.start = moment(args.event.start).tz(timeZone);
            blockedTime.end = moment(args.event.end).tz(timeZone);
            if (args.newResource?.id) {
                const newStaff = props.staff.find(staffAvailable => staffAvailable.id === Number(args.newResource?.id));
                blockedTime.staff = newStaff
                    ? {
                          id: newStaff?.id ?? 0,
                          firstName: newStaff?.firstName ?? '',
                          lastName: newStaff?.lastName ?? '',
                          image: newStaff?.avatar ?? ''
                      }
                    : blockedTime.staff;
            }
            props.onRescheduledBlocked && props.onRescheduledBlocked(blockedTime, args.revert);
        } else {
            props.onRescheduled &&
                props.onRescheduled(
                    args.event.extendedProps.appointmentId,
                    moment(args.event.start).tz(timeZone),
                    args.revert,
                    args.newResource?.id ? Number(args.newResource?.id) : undefined
                );
        }
    };

    const dateClickHandler = (args: DateClickArg, timeZone: string) => {
        const at = moment(args.date).tz(timeZone);
        const quarter = Math.trunc(at.minutes() / 15);
        at.minutes(quarter * 15);
        props.onBook && props.onBook(at, args.resource?.id !== undefined ? Number(args.resource?.id) : undefined);
    };

    const fullCalendarComponent = useMemo(
        () => (
            <FullCalendar
                ref={fullCalendarRef}
                schedulerLicenseKey="CC-Attribution-NonCommercial-NoDerivatives"
                plugins={[scrollGridPlugin, momentTimezonePlugin, resourceTimeGridPlugin, interactionPlugin].filter(
                    p => !props.readOnly || p.name !== interactionPlugin.name
                )}
                initialView="resourceTimeGridDay"
                timeZone={props.timeZone}
                now={getCalendarNow}
                editable={!props.readOnly}
                eventStartEditable={props.loadingAppointmentIds?.length === 0}
                eventDurationEditable={false}
                slotDuration="0:15:00"
                slotLabelInterval="1:00:00"
                snapDuration="0:10:00"
                expandRows={true}
                height="100%"
                headerToolbar={false}
                allDaySlot={false}
                stickyHeaderDates={false}
                dayCellClassNames={
                    props.readOnly
                        ? [fullcalendarClasses.dayCell, fullcalendarClasses.notClickeable]
                        : [fullcalendarClasses.dayCell]
                }
                slotLabelFormat={{
                    hour: 'numeric',
                    omitZeroMinute: false
                }}
                slotLabelClassNames={[fullcalendarClasses.slotLabel]}
                slotLaneClassNames={
                    props.readOnly
                        ? [fullcalendarClasses.slotLane, fullcalendarClasses.notClickeable]
                        : [fullcalendarClasses.slotLane]
                }
                nowIndicator={true}
                nowIndicatorClassNames={[classes.nowIndicator]}
                nowIndicatorContent={args => (
                    <NowIndicatorFullCalendar
                        {...args}
                        timeZone={props.timeZone}
                    />
                )}
                scrollTime={scrollTime}
                events={events}
                eventContent={event => renderEvent(event, props.appointments, props.loadingAppointmentIds)}
                eventClassNames={[props.isSlotsView ? fullcalendarClasses.slotEvent : fullcalendarClasses.event]}
                eventClick={eventClickHandler}
                eventDrop={args => dropHandler(args, props.timeZone)}
                dateClick={args => dateClickHandler(args, props.timeZone)}
                resources={resources}
                resourceOrder="order"
                resourceLabelContent={args => (
                    <ResourceHeaderFullCalendar
                        {...args}
                        date={props.date}
                        businessArea={props.businessArea}
                        showPetsCountBadge={props.showPetsCountBadge}
                    />
                )}
                resourceLabelClassNames={[classes.resourceHeader]}
                dayMinWidth={200}
            />
        ),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [events, resources, props.timeZone]
    );

    return (
        <div
            ref={wrapperRef}
            className={clsx(classes.container, props.isSlotsView && classes.slotsViewContainer)}
        >
            {fullCalendarComponent}
        </div>
    );
};

export default DayFullCalendar;
